import { unwrap } from "@sagot/api-client";
import { API_ORIGIN, APP_URLS, consoleRoutes, DEVELOPER_API_PATH, DEVELOPER_API_RATE_LIMIT_PER_MINUTE } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, EmptyState, Icon, TableSkeletonRows, toast, cn } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useMembers } from "../../../../roles/use-roles";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceContext, useWorkspaceId } from "../../../../session/workspace-context";
import { useAgentList, useFormatters, wsKeys, type ApiKeyOut } from "../../shared";
import { DirectionNote } from "../settings-direction";
import { useApiKeys, useInvalidate } from "../use-settings";
import { CreateKeyDialog } from "./create-key-dialog";
import { KeyAllowlistDialog } from "./key-allowlist-dialog";
import { Gate, PanelError, useCanManage, CodeSnippet, TableScroll } from "./panel-kit";
import { RetireBadge, RotateKeyDialog } from "./rotate-key-dialog";

const COLUMNS = 5;
const SHOWN_RANGES = 2;

/** Quick-start request against the public API host (the key stays server-side). */
export function quickStartSnippet(agentId: string, sample: string): string {
  return [
    `curl ${API_ORIGIN}${DEVELOPER_API_PATH}/agents/${agentId}/chat \\`,
    `  -H "Authorization: Bearer $SAGOT_API_KEY" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '${JSON.stringify({ message: sample })}'`,
  ].join("\n");
}

/** The ranges a key accepts (first two, then "+N more"), or "Any address" while the list is empty. */
export function AllowedRanges({ ranges }: { ranges: readonly string[] }) {
  const { t } = useTranslation();
  if (ranges.length === 0) return <span className="text-fg-muted">{t("settings.api.anyAddress")}</span>;
  const rest = ranges.length - SHOWN_RANGES;
  return (
    <span className="flex flex-col font-mono text-xs" data-testid="allowed-ranges" title={ranges.join(", ")}>
      {ranges.slice(0, SHOWN_RANGES).map((range) => (
        <span key={range}>{range}</span>
      ))}
      {rest > 0 ? <span className="font-sans text-fg-muted">{t("settings.api.moreRanges", { count: rest })}</span> : null}
    </span>
  );
}

interface KeyRowProps {
  apiKey: ApiKeyOut;
  creator: string | null;
  manage: ReturnType<typeof useCanManage>;
  revoking: boolean;
  onRevoke: () => void;
  onSecurity: () => void;
  onRotate: () => void;
}

function KeyRow({ apiKey, creator, manage, revoking, onRevoke, onSecurity, onRotate }: KeyRowProps) {
  const { t } = useTranslation();
  const format = useFormatters();
  const revoked = !apiKey.active;
  const disabled = !manage.allowed;
  return (
    <tr className={cn(revoked && "bg-surface-2 text-fg-muted")} data-revoked={revoked || undefined}>
      <td>
        <span className="font-semibold">{apiKey.name}</span>
        {apiKey.signing_configured && !revoked ? (
          <Badge tone={apiKey.signing_required ? "success" : "brand"} className="ml-2 align-middle" data-testid="signing-badge">
            <Icon name="shield-check" className="size-3" />
            {apiKey.signing_required ? t("settings.api.signingRequiredBadge") : t("settings.api.signingOptionalBadge")}
          </Badge>
        ) : null}
        <div className={cn("whitespace-nowrap font-mono text-xs text-fg-muted", revoked && "line-through")}>{apiKey.display}</div>
        {apiKey.rotation && !revoked ? (
          <div className="mt-1">
            <RetireBadge until={apiKey.rotation.previous_valid_until} />
          </div>
        ) : null}
      </td>
      <td>
        <span className="flex flex-wrap gap-1">
          {apiKey.scopes.map((scope) => (
            <Badge key={scope} className="font-mono">
              {scope}
            </Badge>
          ))}
        </span>
      </td>
      <td>
        <AllowedRanges ranges={apiKey.allowed_ip_ranges} />
      </td>
      <td className="whitespace-nowrap text-fg-muted">
        {apiKey.last_used_at ? format.relative(apiKey.last_used_at) : t("settings.api.neverUsed")}
        <div className="text-xs text-fg-subtle">{[t("settings.api.createdOn", { date: format.shortDate(apiKey.created_at) }), creator].filter(Boolean).join(" · ")}</div>
      </td>
      <td className="text-right">
        {revoked ? (
          <Badge tone="danger">
            <Icon name="x-circle" className="size-3" />
            {apiKey.revoked_at ? t("settings.api.revoked") : t("settings.api.expired")}
          </Badge>
        ) : (
          <Gate reason={manage.reason}>
            <span className="inline-flex max-w-44 flex-wrap justify-end gap-1">
              <Button variant="ghost" size="sm" disabled={disabled} onClick={onSecurity} aria-label={t("settings.api.securityOne", { name: apiKey.name })}>
                {t("settings.api.security")}
              </Button>
              <Button variant="ghost" size="sm" disabled={disabled} onClick={onRotate} aria-label={t("settings.api.rotateOne", { name: apiKey.name })}>
                {t("settings.api.rotate")}
              </Button>
              <Button variant="ghost" size="sm" className="text-danger" disabled={disabled || revoking} onClick={onRevoke} aria-label={t("settings.api.revokeOne", { name: apiKey.name })}>
                {t("settings.api.revoke")}
              </Button>
            </span>
          </Gate>
        )}
      </td>
    </tr>
  );
}

function QuickStart() {
  const { t } = useTranslation();
  const agents = useAgentList();
  const agentId = agents.data?.[0]?.id ?? "<agent-id>";
  return (
    <section className="card card-body" aria-labelledby="quickstart-title">
      <h2 id="quickstart-title" className="card-title">
        {t("settings.api.quickStart")}
      </h2>
      <CodeSnippet className="mt-3" code={quickStartSnippet(agentId, t("settings.api.sampleQuestion"))} label={t("settings.api.quickStart")} />
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-fg-muted">
        <span>{t("settings.api.rateLimit", { count: DEVELOPER_API_RATE_LIMIT_PER_MINUTE })}</span>
        <AppLink href={APP_URLS.docsApi} className="font-semibold text-primary-ink hover:underline" target="_blank" rel="noreferrer">
          {t("settings.api.reference")}
        </AppLink>
      </div>
    </section>
  );
}

/** API keys panel: list, create (show once), allowlist + signing, rotate in place, revoke + quick start. */
export function ApiKeysPanel() {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const message = useErrorMessage();
  const invalidate = useInvalidate();
  const keys = useApiKeys();
  const manage = useCanManage("api_keys.manage");
  const permissions = usePermissions();
  const canSeeMembers = permissions.can("team.view");
  const { summary } = useWorkspaceContext();
  const members = useMembers();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<{ kind: "security" | "rotate"; id: string } | null>(null);
  const editedKey = editing ? keys.data?.find((candidate) => candidate.id === editing.id) : undefined;
  const refresh = () => invalidate(wsKeys.apiKeys(wsId));
  const nameOf = (userId: string | null) => (canSeeMembers && userId ? (members.data?.find((m) => m.user_id === userId)?.name ?? null) : null);
  const revoke = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/api-keys/{key_id}", { params: { path: { key_id: id } } })),
    onSuccess: async () => {
      toast.warning(t("settings.api.revokedToast"));
      await invalidate(wsKeys.apiKeys(wsId));
    },
    onError: (caught) => toast.error(message(caught)),
  });
  if (keys.isError) return <PanelError error={keys.error} onRetry={() => void keys.refetch()} />;
  const createButton = (
    <Gate reason={manage.reason}>
      <Button size="sm" disabled={!manage.allowed} onClick={() => { setCreating(true); }}>
        <Icon name="plus" />
        {t("settings.api.createKey")}
      </Button>
    </Gate>
  );
  return (
    <>
      <section className="card overflow-hidden" aria-labelledby="api-title">
        <div className="card-header">
          <div>
            <h2 id="api-title" className="card-title">
              {t("settings.api.title")}
            </h2>
            <p className="mt-0.5 text-xs text-fg-subtle">{t("settings.api.subtitle")}</p>
            <DirectionNote
              className="mt-1"
              testId="api-direction-note"
              lead={t("settings.api.directionLead")}
              linkLabel={t("settings.api.directionLink")}
              tail={t("settings.api.directionTail")}
              href={permissions.can("connectors.view") ? consoleRoutes.connectors(summary.slug) : null}
            />
          </div>
          {createButton}
        </div>
        {keys.data?.length === 0 ? (
          <EmptyState icon="key" headingLevel={3} title={t("settings.api.emptyTitle")} description={t("settings.api.emptyBody")} action={createButton} />
        ) : (
          <TableScroll label={t("settings.api.title")}>
            <table className="table" data-testid="api-keys">
              <thead>
                <tr>
                  <th>{t("settings.api.colName")}</th>
                  <th>{t("settings.api.colScopes")}</th>
                  <th>{t("settings.api.colAllowed")}</th>
                  <th>{t("settings.api.colLastUsed")}</th>
                  <th>
                    <span className="sr-only">{t("settings.colActions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {keys.isPending ? (
                  <TableSkeletonRows rows={3} columns={COLUMNS} />
                ) : (
                  keys.data.map((apiKey) => (
                    <KeyRow
                      key={apiKey.id}
                      apiKey={apiKey}
                      creator={nameOf(apiKey.created_by)}
                      manage={manage}
                      revoking={revoke.isPending}
                      onRevoke={() => { revoke.mutate(apiKey.id); }}
                      onSecurity={() => { setEditing({ kind: "security", id: apiKey.id }); }}
                      onRotate={() => { setEditing({ kind: "rotate", id: apiKey.id }); }}
                    />
                  ))
                )}
              </tbody>
            </table>
          </TableScroll>
        )}
      </section>
      <QuickStart />
      {creating ? <CreateKeyDialog onClose={() => { setCreating(false); }} onCreated={refresh} /> : null}
      {editing && editedKey?.active ? (
        editing.kind === "security" ? (
          <KeyAllowlistDialog key={editedKey.id} apiKey={editedKey} onClose={() => { setEditing(null); }} onChanged={refresh} />
        ) : (
          <RotateKeyDialog key={editedKey.id} apiKey={editedKey} onClose={() => { setEditing(null); }} onRotated={refresh} />
        )
      ) : null}
    </>
  );
}
